<template>
  <Dialog
    v-if="open"
    :title="`Manage Cookies`"
    :headless="false"
    body-class="overflow-auto text-foreground"
    class="fixed min-w-xl"
    @close="close">
    <div class="px-6 py-6 space-y-6">
      <p class="text-sm text-forecolor">
        {{ $t("Choose which cookies you want to allow. Your preferences will be saved and applied immediately.") }}
      </p>
      <!-- Analytics Cookies -->
      <div class="flex items-start gap-4">
        <div class="flex-1">
          <div class="flex items-center gap-3 mb-2">
            <Switch
              v-model="preferences.analytics"
              label=""
              :aria-label="$t('analytics_cookies') || 'Analytics cookies'" />
            <h3 class="font-medium text-forecolor">
              {{ $t("Analytics Cookies") }}
            </h3>
          </div>
          <p class="text-sm text-forecolor ml-11">
            {{
              $t(
                "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. Includes Google Analytics and Microsoft Clarity."
              )
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
      <Button @click="close">
        {{ $t("Cancel") }}
      </Button>
      <Button @click="savePreferences">
        {{ $t("Save preferences") }}
      </Button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { Switch, Button, Dialog } from "@xs2event/js-lib/components";

interface CookiePreferences {
  analytics: boolean;
}

interface Props {
  open: boolean;
}

interface Emits {
  (e: "update:open", value: boolean): void;
  (e: "save", preferences: CookiePreferences): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const PREFERENCES_KEY = "cookiePreferences";

const getStoredPreferences = (): CookiePreferences => {
  try {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { analytics: Boolean(parsed?.analytics) };
    }
  } catch (e) {
    console.error("Failed to parse cookie preferences", e);
  }
  return {
    analytics: true,
  };
};

const preferences = ref<CookiePreferences>(getStoredPreferences());

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      preferences.value = getStoredPreferences();
    }
  }
);

const close = () => {
  emit("update:open", false);
};

const savePreferences = () => {
  emit("save", preferences.value);
};
</script>
